import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { formatCurrency } from '../../../utils/formatters';
import { get, split } from 'lodash';
import Button from '../../../../../ui/canon/Button';
import Card from '../../../../../ui/canon/Card';

class DealTeaserSlideshow extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      visibleElements: new Set(),
      scrollY: 0,
      scrollDirection: 'down',
      lastScrollY: 0
    };
    this.containerRef = React.createRef();
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.containerRef.current) {
      this.containerRef.current.removeEventListener('scroll', this.handleScroll);
    }
  }

  // Initialize scroll animations
  initializeScrollAnimations = () => {
    const observerOptions = {
      root: this.containerRef.current,
      rootMargin: '-5% 0px -15% 0px',
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
    };
    
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const element = entry.target;
        const scrollId = element.dataset.scrollId;
        
        if (entry.isIntersecting) {
          this.setState((prevState) => {
            if (prevState.visibleElements.has(scrollId)) return null;
            return {
              visibleElements: new Set([...prevState.visibleElements, scrollId])
            };
          });

          requestAnimationFrame(() => {
            element.style.transition = 'all 1.2s cubic-bezier(0.16, 1, 0.3, 1)';
            element.style.opacity = '1';
            element.style.transform = 'translateY(0px) scale(1)';
          });

          // Reveal once, then stop observing to prevent rapid flicker
          this.observer.unobserve(element);
        }
      });
    }, observerOptions);
    
    setTimeout(() => {
      if (this.containerRef.current) {
        const scrollElements = this.containerRef.current.querySelectorAll('[data-scroll-id]');
        scrollElements.forEach((el, index) => {
          el.style.opacity = '0';
          el.style.transform = 'translateY(60px) scale(0.95)';
          el.style.transition = `all 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.1}s`;
          this.observer.observe(el);
        });
      }
    }, 100);
  };

  // Scroll tracking
  handleScroll = () => {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        const currentScrollY = this.containerRef.current?.scrollTop || 0;
        this.setState(prevState => ({
          scrollDirection: currentScrollY > prevState.lastScrollY ? 'down' : 'up',
          scrollY: currentScrollY,
          lastScrollY: currentScrollY
        }));
        this.ticking = false;
      });
      this.ticking = true;
    }
  };

  componentDidMount() {
    this.initializeScrollAnimations();
    if (this.containerRef.current) {
      this.containerRef.current.addEventListener('scroll', this.handleScroll, { passive: true });
    }
  }

  // Helper function to check if element is visible
  isVisible = (elementId) => this.state.visibleElements.has(elementId);

  // Check if buyer can download PDF
  canDownloadPDF = () => {
    const { dealInterest } = this.props;
    return dealInterest?.workflow_state === 'negotiating';
  };

  // Mock data for missing fields
  getData = (field, defaultValue = 'Not provided') => {
    const { deal } = this.props;
    const mockData = {
      year_established: deal?.inventory_user?.user?.year_1_gross ? (new Date().getFullYear() - 5) : 2020,
      commission_percentage: 70,
      fee_percentage: 30,
      team_size: 2,
      has_business_continuity_plan: true,
      seller_transition_period_months: 12,
      assets_under_management: deal?.inventory_user?.user?.annual_revenue ? deal?.inventory_user?.user?.annual_revenue * 10 : null,
      gross_margin: 60,
      operating_margin: 30,
      net_income: deal?.inventory_user?.user?.annual_revenue ? deal?.inventory_user?.user?.annual_revenue * 0.3 : null,
    };
    
    return deal?.[field] || mockData[field] || defaultValue;
  };

  // PDF Generation via server endpoint
  generatePDF = () => {
    const dealSlug = this.props.deal?.deal_name || this.props.deal?.id;
    if (!dealSlug) {
      console.error('No deal identifier available for PDF generation');
      return;
    }
    
    // Create download link and trigger it
    const downloadUrl = `/succession/deals/${dealSlug}/teaser.pdf`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${this.props.deal?.deal_name || 'deal-teaser'}.pdf`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    const { deal, company, currentUser, intl } = this.props;
    const canDownloadPDF = this.canDownloadPDF();
    const hideListPrice =
      (deal?.deal_type === 'continuity' && company?.hide_list_price_in_deal_room_wizard_continuity) ||
      (deal?.deal_type === 'succession' && company?.hide_list_price_in_deal_room_wizard_succession) ||
      (deal?.deal_type === 'sale' && company?.hide_list_price_in_deal_room_wizard_sale);

    return (
      <div className="bg-[var(--succession-bg-primary)] rounded-lg shadow-sm overflow-hidden">
        {/* Enhanced CSS for Apple-style animations */}
        <style jsx>{`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes fade-in-up {
            from { 
              opacity: 0; 
              transform: translateY(30px); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0); 
            }
          }
          
          @keyframes float {
            0%, 100% { 
              transform: translateY(0px) rotate(0deg); 
            }
            33% { 
              transform: translateY(-10px) rotate(120deg); 
            }
            66% { 
              transform: translateY(5px) rotate(240deg); 
            }
          }
          
          @keyframes gradient-shift {
            0%, 100% { 
              background-position: 0% 50%; 
            }
            50% { 
              background-position: 100% 50%; 
            }
          }
          
          @keyframes shimmer {
            0% { 
              transform: translateX(-100%); 
            }
            100% { 
              transform: translateX(100%); 
            }
          }
          
          @keyframes pulse-slow {
            0%, 100% { 
              opacity: 0.1; 
              transform: scale(1); 
            }
            50% { 
              opacity: 1; 
              transform: scale(1.05); 
            }
          }
          
          @keyframes number-reveal {
            0% { 
              opacity: 0; 
              transform: translateY(20px) scale(0.8); 
            }
            100% { 
              opacity: 1; 
              transform: translateY(0px) scale(1); 
            }
          }
          
          @keyframes bounce-slow {
            0%, 100% { 
              transform: translateY(0px); 
            }
            50% { 
              transform: translateY(-10px); 
            }
          }
          
          @keyframes slow-spin {
            from { 
              transform: rotate(0deg); 
            }
            to { 
              transform: rotate(360deg); 
            }
          }
          
          .animate-fade-in {
            animation: fade-in 1s ease-out forwards;
          }
          
          .animate-fade-in-up {
            animation: fade-in-up 1s ease-out forwards;
          }
          
          .animate-float {
            animation: float 6s ease-in-out infinite;
          }
          
          .animate-gradient-shift {
            background-size: 200% 200%;
            animation: gradient-shift 8s ease infinite;
          }
          
          .animate-shimmer {
            animation: shimmer 2s ease-in-out infinite;
          }
          
          .animate-pulse-slow {
            animation: pulse-slow 4s ease-in-out infinite;
          }
          
          .animate-number-reveal {
            animation: number-reveal 1.5s ease-out forwards;
          }
          
          .animate-bounce-slow {
            animation: bounce-slow 3s ease-in-out infinite;
          }
          
          .animate-slow-spin {
            animation: slow-spin 20s linear infinite;
          }
          
          .animation-delay-300 { animation-delay: 300ms; }
          .animation-delay-500 { animation-delay: 500ms; }
          .animation-delay-700 { animation-delay: 700ms; }
          .animation-delay-1000 { animation-delay: 1000ms; }
          .animation-delay-1200 { animation-delay: 1200ms; }
          
          .delay-0 { animation-delay: 0ms; }
          .delay-500 { animation-delay: 500ms; }
          .delay-1000 { animation-delay: 1000ms; }
          .delay-1500 { animation-delay: 1500ms; }
          .delay-2000 { animation-delay: 2000ms; }
          .delay-2500 { animation-delay: 2500ms; }
          .delay-3000 { animation-delay: 3000ms; }
          .delay-3500 { animation-delay: 3500ms; }
          
          /* PDF-specific styles */
          @media print {
            .pdf-hide {
              display: none !important;
            }
            
            .page-break-before {
              page-break-before: always;
            }
            
            .page-break-after {
              page-break-after: always;
            }
            
            body {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            * {
              animation: none !important;
              transition: none !important;
            }
          }
        `}</style>

        {/* Infographic Content Container */}
        <div 
          id="deal-slideshow-content"
          ref={this.containerRef}
          className="relative bg-[var(--succession-bg-secondary)] overflow-y-auto h-[80vh] p-6 space-y-8"
        >
          {/* Hero Investment Overview */}
          <Card className="p-6 space-y-4 relative overflow-hidden" data-scroll-id="hero">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-3xl font-thin text-[var(--succession-text-primary)]">
                {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.deal_teaser_heading' })}
              </h2>
              {canDownloadPDF && (
                <Button
                  onClick={this.generatePDF}
                  variant="ghost"
                  size="sm"
                  className="animate-fade-in animation-delay-500"
                >
                  <svg className="w-4 h-4 mr-1 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                  </svg>
                  {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.download_pdf_button' })}
                </Button>
              )}
            </div>

            <div className="relative h-80 w-full bg-gradient-to-r from-black via-black to-black overflow-hidden rounded-lg">
              {/* Animated particles background */}
              <div className="absolute inset-0 pdf-hide">
                <div className="absolute top-10 left-10 w-2 h-2 bg-white/20 rounded-full animate-float delay-0"></div>
                <div className="absolute top-20 right-20 w-1 h-1 bg-white/30 rounded-full animate-float delay-500"></div>
                <div className="absolute top-40 left-1/3 w-1.5 h-1.5 bg-white/15 rounded-full animate-float delay-1000"></div>
                <div className="absolute bottom-20 right-1/4 w-2 h-2 bg-white/10 rounded-full animate-float delay-1500"></div>
              </div>
              
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60 animate-gradient-shift pdf-hide"></div>
              <div className="absolute inset-0 bg-black/40"></div>
              
              <div className="relative h-full flex items-center justify-center text-white">
                <div className="text-center max-w-4xl mx-auto px-6 animate-fade-in-up">
                  <div className="mb-4 opacity-0 animate-fade-in animation-delay-300">
                    <span className="inline-block px-3 py-1 bg-white/10 backdrop-blur rounded-full text-xs font-medium tracking-wide border border-white/20">
                      {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.premier_succession_opportunity' })}
                    </span>
                  </div>
                  <h1 className="text-xl md:text-2xl font-thin mb-4 tracking-tight leading-none opacity-0 animate-fade-in animation-delay-500">
                    {deal?.headline || 'PRIME OPPORTUNITY'}
                  </h1>
                  {deal?.sub_headline && !company?.teaser_hide_business_overview_question && (
                    <p className="text-lg font-light opacity-90 mb-6 max-w-2xl mx-auto leading-relaxed opacity-0 animate-fade-in animation-delay-700">
                      {deal.sub_headline}
                    </p>
                  )}
                  {!hideListPrice && (
                    <div className="text-3xl font-thin mb-3 opacity-0 animate-number-reveal animation-delay-1000">
                      <span className="relative">
                        {deal.list_price ? formatCurrency(deal.list_price) : intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.price_on_application' })}
                      </span>
                    </div>
                  )}
                  <div className="text-xs font-light tracking-widest opacity-80 opacity-0 animate-fade-in animation-delay-1200">
                    {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.succession_opportunity_label' })} • {deal?.deal_name || 'CONFIDENTIAL'}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Investment Summary */}
          <Card className="p-6" data-scroll-id="practice-overview">
            <div className="space-y-4">
              <div className="grid lg:grid-cols-3 gap-8 items-start">
                <div className={`lg:col-span-2 transition-all duration-1000 ${this.isVisible('practice-overview') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                  <h2 className="text-3xl font-thin mb-6 text-[var(--succession-text-primary)] hover:text-[var(--succession-text-secondary)] transition-colors duration-500">
                    {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.practice_overview_title' })}
                  </h2>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-lg leading-relaxed text-[var(--succession-text-secondary)] font-light hover:text-[var(--succession-text-primary)] transition-colors duration-300">
                      {deal?.summary_of_business || intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.practice_overview_default_summary' })}
                    </p>
                  </div>
                </div>
                <div className={`bg-[var(--succession-bg-secondary)] p-6 rounded-none border-l-4 border-black hover:bg-[var(--succession-hover-bg)] hover:shadow-lg transition-all duration-1000 delay-200 ${this.isVisible('practice-overview') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                  <h3 className="text-xl font-thin mb-4 text-[var(--succession-text-primary)]">
                    {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.key_practice_metrics_title' })}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-xs font-medium text-[var(--succession-text-muted)] uppercase tracking-wide mb-1">
                        {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.annual_revenue_label' })}
                      </div>
                      <div className="text-2xl font-thin text-[var(--succession-text-primary)]">{formatCurrency(deal.inventory_user?.user?.annual_revenue)}</div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-[var(--succession-text-muted)] uppercase tracking-wide mb-1">
                        {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.client_base_label' })}
                      </div>
                      <div className="text-2xl font-thin text-[var(--succession-text-primary)]">
                        {deal.inventory_user?.user?.number_of_clients || intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.confidential' })}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-[var(--succession-text-muted)] uppercase tracking-wide mb-1">
                        {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.established_label' })}
                      </div>
                      <div className="text-2xl font-thin text-[var(--succession-text-primary)]">{
                        deal.inventory_user?.user?.years_practicing ? 
                          (new Date().getFullYear() - deal.inventory_user.user.years_practicing).toString() : '-'
                        }</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Business Fundamentals */}
          {/* Plain div instead of canon Card because Card's bg/text Succession tokens are !important and override bg-black/text-white, making text illegible against the inner black gradient backdrop. */}
          <div className="p-6 relative overflow-hidden rounded-lg shadow-succession-md bg-black text-white" data-scroll-id="business-fundamentals">
            {/* Animated background patterns */}
            <div className="absolute inset-0 pdf-hide">
              <div className="absolute top-0 left-0 w-full h-full opacity-5">
                <div className="absolute top-10 left-10 w-24 h-24 border border-white/20 rounded-full animate-pulse-slow"></div>
                <div className="absolute top-40 right-20 w-16 h-16 border border-white/15 rounded-full animate-pulse-slow delay-1000"></div>
                <div className="absolute bottom-20 left-1/3 w-32 h-32 border border-white/10 rounded-full animate-pulse-slow delay-2000"></div>
                <div className="absolute bottom-40 right-10 w-20 h-20 border border-white/25 rounded-full animate-pulse-slow delay-3000"></div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-black via-black to-black animate-gradient-shift"></div>
            </div>
            <div className="relative max-w-6xl mx-auto px-6">
              <div className={`text-center mb-12 transition-all duration-1000 ${this.isVisible('business-fundamentals') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="text-3xl font-thin mb-4">
                  {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.business_fundamentals_title' })}
                </h2>
                <div className="w-24 h-px bg-white mx-auto"></div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[
                  ...(!hideListPrice ? [{
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.list_price_label' }),
                    value: deal.list_price ? formatCurrency(deal.list_price) : intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.tbd' }),
                    unit: ''
                  }] : []),
                  {
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.location_label' }),
                    value: `${deal.inventory_user?.user?.city}, ${deal.inventory_user?.user?.province}`,
                    unit: ''
                  },
                  ...(deal.inventory_user?.affiliation ? [{
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.affiliation_label' }),
                    value: deal.inventory_user.affiliation,
                    unit: ''
                  }] : []),
                  ...(deal.inventory_user?.user?.assets_under_management ? [{
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.aum_label' }),
                    value: formatCurrency(deal.inventory_user.user.assets_under_management),
                    unit: ''
                  }] : []),
                  {
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.number_of_clients_label' }),
                    value: deal.inventory_user?.user?.number_of_clients || intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.confidential' }),
                    unit: ''
                  },
                  {
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.year_established_label' }),
                    value: deal.inventory_user?.user?.years_practicing ?
                      (new Date().getFullYear() - deal.inventory_user.user.years_practicing).toString() : '-',
                    unit: ''
                  },
                  {
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.commission_percentage_label' }),
                    value: deal.inventory_user?.user?.percent_commission ? Math.round(deal.inventory_user?.user?.percent_commission).toString() : '-',
                    unit: '%'
                  },
                  {
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.fee_percentage_label' }),
                    value: deal.inventory_user?.user?.percent_fee_based ? Math.round(deal.inventory_user?.user?.percent_fee_based).toString() : '-',
                    unit: '%'
                  },
                  ...(deal.inventory_user?.user?.team_size && deal.inventory_user.user.team_size > 0 ? [{
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.team_size_label' }),
                    value: deal.inventory_user.user.team_size.toString(),
                    unit: ''
                  }] : []),
                  ...(deal.inventory_user?.user?.number_of_employees && deal.inventory_user.user.number_of_employees > 0 ? [{
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.number_of_employees_label' }),
                    value: deal.inventory_user.user.number_of_employees.toString(),
                    unit: ''
                  }] : []),
                  {
                    metric: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.business_continuity_plan_label' }),
                    value: this.getData('has_business_continuity_plan')
                      ? intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.true' })
                      : intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.false' }),
                    unit: ''
                  }
                ].map((item, index) => (
                  <div key={index} className={`text-center transition-all duration-1000 hover:scale-110 ${this.isVisible('business-fundamentals') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: `${300 + index * 100}ms`}}>
                    <div className="text-2xl font-thin mb-2 hover:scale-110 transition-transform duration-300">{item.value}<span className="text-sm">{item.unit}</span></div>
                    <div className="text-xs font-light tracking-wide opacity-80 uppercase">{item.metric}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Financial Performance */}
          <Card className="p-6" data-scroll-id="financial-performance">
            <div className="space-y-6">
              <div className={`transition-all duration-1000 ${this.isVisible('financial-performance') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="text-3xl font-thin mb-4 text-[var(--succession-text-primary)]">
                  {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.financial_performance_title' })}
                </h2>
                <div className="w-24 h-px bg-[var(--succession-text-primary)]"></div>
              </div>
              
              {/* Trailing 12-Month Revenue Breakdown */}
              <div className="space-y-4">
                <h3 className="text-2xl font-thin mb-6 text-[var(--succession-text-primary)]">
                  {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.trailing_12_month_title' })}
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {[
                    {
                      label: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.t12_revenue_label' }),
                      amount: formatCurrency(deal.inventory_user?.user?.annual_revenue),
                      description: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.t12_revenue_description' }),
                      highlight: true
                    },
                    {
                      label: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.t12_recurring_revenue_label' }),
                      amount: formatCurrency(deal.inventory_user?.user?.recurring_revenue),
                      description: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.t12_recurring_revenue_description' }),
                    },
                    {
                      label: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.fee_based_revenue_label' }),
                      amount: formatCurrency(deal.inventory_user?.user?.fee_based_revenue),
                      description: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.fee_based_revenue_description' }),
                    },
                    {
                      label: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.trailer_revenue_label' }),
                      amount: formatCurrency(deal.inventory_user?.user?.trailer_revenue),
                      description: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.trailer_revenue_description' }),
                    },
                    {
                      label: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.transactional_revenue_label' }),
                      amount: formatCurrency(deal.inventory_user?.user?.transactional_revenue),
                      description: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.transactional_revenue_description' }),
                    }
                  ].map((item, index) => (
                    <div key={index} className={`${item.highlight ? 'border-2 border-blue-500 bg-[var(--succession-bg-secondary)]' : 'border border-[var(--succession-border)] bg-[var(--succession-bg-primary)]'} p-4 rounded-lg transition-all duration-1000 hover:shadow-xl hover:-translate-y-2 ${this.isVisible('financial-performance') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: `${200 + index * 100}ms`}}>
                      <div className="text-lg font-bold text-[var(--succession-text-primary)] mb-2">{item.amount}</div>
                      <div className="text-sm font-semibold text-[var(--succession-text-primary)] mb-1">{item.label}</div>
                      <div className="text-xs text-[var(--succession-text-secondary)]">{item.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gross Production Chart */}
              <div className={`border-t border-[var(--succession-border)] pt-6 mt-6 bg-[var(--succession-bg-secondary)] p-8 transition-all duration-1000 delay-[600ms] ${this.isVisible('financial-performance') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                <h3 className="text-2xl font-thin mb-6 text-[var(--succession-text-primary)]">
                  {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.gross_production_history_title' })}
                </h3>
                <div className="flex items-end justify-between h-48 border-b border-[var(--succession-border)]">
                  {[
                    { year: new Date().getFullYear() - 3, value: deal.inventory_user?.user?.year_4_gross || 0, label: '2021' },
                    { year: new Date().getFullYear() - 2, value: deal.inventory_user?.user?.year_3_gross || 0, label: '2022' },
                    { year: new Date().getFullYear() - 1, value: deal.inventory_user?.user?.year_2_gross || 0, label: '2023' },
                    { year: new Date().getFullYear(), value: deal.inventory_user?.user?.year_1_gross || 0, label: '2024' }
                  ].map((item, index) => {
                    const maxValue = Math.max(
                      deal.inventory_user?.user?.year_1_gross || 0,
                      deal.inventory_user?.user?.year_2_gross || 0,
                      deal.inventory_user?.user?.year_3_gross || 0,
                      deal.inventory_user?.user?.year_4_gross || 0
                    );
                    const height = maxValue > 0 ? (item.value / maxValue) * 160 : 20;
                    
                    return (
                      <div key={index} className="flex flex-col items-center flex-1 group">
                        <div 
                          className="w-12 bg-gradient-to-t from-blue-600 to-blue-400 transition-all duration-1000 ease-out relative overflow-hidden group-hover:from-blue-700 group-hover:to-blue-500 rounded-t-md flex items-center justify-center"
                          style={{ height: `${height}px` }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/10 to-transparent animate-shimmer"></div>
                          <div className="text-xs font-semibold text-white z-10 transform -rotate-90 whitespace-nowrap">
                            {formatCurrency(item.value)}
                          </div>
                        </div>
                        <div className="mt-3 text-sm font-medium text-[var(--succession-text-primary)]">{item.year}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 text-center">
                  <p className="text-sm text-[var(--succession-text-secondary)]">
                    {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.gross_production_trend' })}
                  </p>
                </div>
              </div>

              {/* Assets Information */}
              {(deal?.assets_included || deal?.intangible_assets_included) && (
                <div className={`border-t border-[var(--succession-border)] pt-6 mt-6 bg-[var(--succession-bg-primary)] border border-[var(--succession-border)] p-6 transition-all duration-1000 delay-[800ms] ${this.isVisible('financial-performance') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                  <h3 className="text-2xl font-thin mb-6 text-[var(--succession-text-primary)]">
                    {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.assets_information_title' })}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {deal?.assets_included && (
                      <div>
                        <h4 className="text-lg font-medium text-[var(--succession-text-primary)] mb-3">
                          {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.assets_included_title' })}
                        </h4>
                        <p className="text-[var(--succession-text-secondary)] leading-relaxed">{deal.assets_included}</p>
                      </div>
                    )}
                    {deal?.intangible_assets_included && (
                      <div>
                        <h4 className="text-lg font-medium text-[var(--succession-text-primary)] mb-3">
                          {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.intangible_assets_title' })}
                        </h4>
                        <p className="text-[var(--succession-text-secondary)] leading-relaxed">{deal.intangible_assets_included}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Specializations */}
          <Card className="p-6" data-scroll-id="products-services">
            <div className="space-y-6">
              <div className={`transition-all duration-1000 ${this.isVisible('products-services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="text-3xl font-thin mb-4 text-[var(--succession-text-primary)]">
                  {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.specializations_title' })}
                </h2>
                <div className="w-24 h-px bg-[var(--succession-text-primary)]"></div>
              </div>
              
              {/* Practice Information */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  // Only include Practice Areas if tag_list exists and has items
                  ...(deal.inventory_user?.user?.tag_list ? [{
                    period: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.practice_areas_period' }),
                    amount: split(deal.inventory_user.user.tag_list, ',')
                      .slice(0, 3)
                      .map(tag =>
                        tag.trim()
                           .replace(/_/g, ' ')
                           .replace(/\b\w/g, l => l.toUpperCase())
                      )
                      .join(', '),
                    label: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.practice_areas_label' })
                  }] : []),

                  // Only include Primary Markets if city or province exists
                  ...((deal.inventory_user?.user?.city || deal.inventory_user?.user?.province) ? [{
                    period: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.primary_markets_period' }),
                    amount: `${deal.inventory_user?.user?.city || ''}${deal.inventory_user?.user?.city && deal.inventory_user?.user?.province ? ', ' : ''}${deal.inventory_user?.user?.province || ''}`,
                    label: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.primary_markets_label' })
                  }] : []),

                  // Only include Specialized Business if niche_market or specialization exists
                  ...((deal.inventory_user?.user?.niche_market || deal.inventory_user?.user?.specialization) ? [{
                    period: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.specialized_business_period' }),
                    amount: deal.inventory_user?.user?.niche_market || deal.inventory_user?.user?.specialization,
                    label: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.specialized_business_label' })
                  }] : []),

                  // Only include Investment Models if discretionary_models or investment_philosophy exists
                  ...((deal.inventory_user?.user?.discretionary_models || deal.inventory_user?.user?.investment_philosophy) ? [{
                    period: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.investment_models_period' }),
                    amount: deal.inventory_user?.user?.discretionary_models
                      ? intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.true' })
                      : deal.inventory_user?.user?.investment_philosophy,
                    label: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.investment_models_label' })
                  }] : []),

                  // Only include What's Your Why if motivation or why_selling exists
                  ...((deal.inventory_user?.user?.motivation || deal.inventory_user?.user?.why_selling) ? [{
                    period: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.whats_your_why_period' }),
                    amount: deal.inventory_user?.user?.motivation || deal.inventory_user?.user?.why_selling,
                    label: intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.whats_your_why_label' })
                  }] : [])
                ].map((item, index) => (
                  <div key={index} className={`border border-[var(--succession-border)] p-6 transition-all duration-1000 hover:shadow-xl hover:-translate-y-2 hover:border-[var(--succession-border-hover)] ${this.isVisible('products-services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: `${200 + index * 100}ms`}}>
                    <div className="text-sm font-medium text-[var(--succession-text-muted)] uppercase tracking-wide mb-2">{item.period}</div>
                    <div className="text-base font-medium text-[var(--succession-text-primary)] mb-2 leading-relaxed">{item.amount}</div>
                    <div className="text-sm text-[var(--succession-text-secondary)] font-light">{item.label}</div>
                  </div>
                ))}
              </div>

              {/* Products and Services Additional Comments */}
              {deal?.products_and_services_comments && (
                <div className={`border-t border-[var(--succession-border)] pt-6 mt-6 bg-[var(--succession-bg-secondary)] p-6 rounded-lg transition-all duration-1000 delay-[600ms] ${this.isVisible('products-services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                  <h3 className="text-xl font-thin mb-3 text-[var(--succession-text-primary)]">
                    {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.products_and_services_title' })}
                  </h3>
                  <p className="text-base text-[var(--succession-text-secondary)] leading-relaxed">{deal.products_and_services_comments}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Client Demographics */}
          <Card className="p-6" data-scroll-id="client-demographics">
            <div className="space-y-6">
              <div className={`transition-all duration-1000 ${this.isVisible('client-demographics') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="text-3xl font-thin mb-4 text-[var(--succession-text-primary)]">
                  {company?.business_type === 'wealth_rj'
                    ? intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.client_demographics_title' })
                    : intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.customer_demographics_title' })}
                </h2>
                <div className="w-24 h-px bg-[var(--succession-text-primary)]"></div>
              </div>
              
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div className={`transition-all duration-1000 ${this.isVisible('client-demographics') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                  <div className="bg-[var(--succession-bg-primary)] p-6 rounded-lg border border-[var(--succession-border)] shadow-sm">
                    <h3 className="text-xl font-thin mb-4 text-[var(--succession-text-primary)]">
                      {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.client_profile_title' })}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center border-b border-[var(--succession-border)] pb-2">
                        <span className="text-[var(--succession-text-secondary)] font-medium">
                          {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.total_clients_label' })}
                        </span>
                        <span className="text-lg font-light text-[var(--succession-text-primary)]">
                          {deal?.inventory_user?.user?.number_of_clients || intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.confidential' })}
                        </span>
                      </div>
                      <div className="flex justify-between items-center border-b border-[var(--succession-border)] pb-2">
                        <span className="text-[var(--succession-text-secondary)] font-medium">
                          {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.average_client_age_label' })}
                        </span>
                        <span className="text-lg font-light text-[var(--succession-text-primary)]">
                          {deal?.inventory_user?.user?.avg_client_age || intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.mixed_age_groups' })}
                        </span>
                      </div>
                      <div className="flex justify-between items-center border-b border-[var(--succession-border)] pb-2">
                        <span className="text-[var(--succession-text-secondary)] font-medium">
                          {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.niche_market_label' })}
                        </span>
                        <span className="text-lg font-light text-[var(--succession-text-primary)]">
                          {deal?.inventory_user?.user?.niche_market || intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.diversified' })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {deal?.customer_demographics_comments && (
                  <div className={`transition-all duration-1000 delay-300 ${this.isVisible('client-demographics') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                    <h3 className="text-xl font-thin mb-4 text-[var(--succession-text-primary)]">
                      {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.additional_comments_title' })}
                    </h3>
                    <div className="bg-[var(--succession-bg-primary)] p-6 rounded-lg border border-[var(--succession-border)] shadow-sm">
                      <p className="text-base text-[var(--succession-text-secondary)] leading-relaxed">{deal.customer_demographics_comments}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Investment Opportunity */}
          <Card className="p-6 relative overflow-hidden" data-scroll-id="succession-opportunity">
            {/* Geometric background animation */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-10 left-10 w-16 h-16 bg-blue-500 rotate-45 animate-slow-spin"></div>
              <div className="absolute top-1/3 right-20 w-12 h-12 bg-green-500 animate-bounce-slow delay-500"></div>
              <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-purple-500 rounded-full animate-pulse-slow delay-1000"></div>
              <div className="absolute bottom-1/3 right-1/3 w-14 h-14 bg-orange-500 rotate-45 animate-slow-spin delay-1500"></div>
            </div>
            <div className="relative max-w-5xl mx-auto px-6">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className={`transition-all duration-1000 ${this.isVisible('succession-opportunity') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                  <h2 className="text-3xl font-thin mb-6 text-[var(--succession-text-primary)]">
                    {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.the_succession_title' })}
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-medium text-[var(--succession-text-primary)] mb-2">
                        {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.ideal_successor_title' })}
                      </h3>
                      <p className="text-base font-light text-[var(--succession-text-secondary)] leading-relaxed">
                        {deal?.ideal_candidate || intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.ideal_successor_default' })}
                      </p>
                    </div>
                    {deal?.growth_opportunities && (
                      <div>
                        <h3 className="text-lg font-medium text-[var(--succession-text-primary)] mb-2">
                          {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.growth_potential_title' })}
                        </h3>
                        <p className="text-base font-light text-[var(--succession-text-secondary)] leading-relaxed">
                          {deal.growth_opportunities}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className={`bg-[var(--succession-bg-primary)] p-8 border border-[var(--succession-border)] transition-all duration-1000 delay-300 hover:shadow-2xl hover:border-[var(--succession-border-hover)] ${this.isVisible('succession-opportunity') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                  <h3 className="text-xl font-thin mb-6 text-[var(--succession-text-primary)]">
                    {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.client_portfolio_title' })}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-[var(--succession-border)] pb-3">
                      <span className="text-[var(--succession-text-secondary)] font-light">
                        {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.total_clients_portfolio_label' })}
                      </span>
                      <span className="text-lg font-thin text-[var(--succession-text-primary)]">
                        {deal?.inventory_user?.user?.number_of_clients || intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.confidential' })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-[var(--succession-border)] pb-3">
                      <span className="text-[var(--succession-text-secondary)] font-light">
                        {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.average_client_age_portfolio_label' })}
                      </span>
                      <span className="text-lg font-thin text-[var(--succession-text-primary)]">
                        {deal?.inventory_user?.user?.avg_client_age || intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.mature' })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-[var(--succession-border)] pb-3">
                      <span className="text-[var(--succession-text-secondary)] font-light">
                        {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.niche_focus_label' })}
                      </span>
                      <span className="text-lg font-thin text-[var(--succession-text-primary)]">
                        {deal?.inventory_user?.user?.niche_market || intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.diversified' })}
                      </span>
                    </div>
                  </div>

                  {deal?.inventory_user?.user?.tag_list && (
                    <div className="mt-6">
                      <h4 className="text-base font-medium text-[var(--succession-text-primary)] mb-3">
                        {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.service_portfolio_title' })}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {split(get(deal, 'inventory_user.user.tag_list', []), ',').map((tag, index) => (
                          <span key={index} className="px-3 py-1 bg-black text-white text-sm font-light tracking-wide hover:opacity-80 hover:scale-110 transition-all duration-300">
                            {tag.trim().replace(/\b\w/g, l => l.toUpperCase()).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Succession Terms */}
          <Card className="p-6 bg-black text-white min-h-[400px]" data-scroll-id="succession-terms">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className={`text-3xl font-thin mb-8 transition-all duration-1000 ${this.isVisible('succession-terms') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.succession_terms_title' })}
              </h2>
              <div className={`grid ${hideListPrice ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-6 mb-8`}>
                {!hideListPrice && (
                  <div className={`transition-all duration-1000 hover:scale-110 ${this.isVisible('succession-terms') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: '200ms'}}>
                    <div className="text-3xl font-thin mb-3">
                      {deal.list_price ? formatCurrency(deal.list_price) : intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.poa' })}
                    </div>
                    <div className="text-sm font-light tracking-wide opacity-80 uppercase">
                      {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.listed_price_label' })}
                    </div>
                  </div>
                )}
                
                <div className="transition-all duration-300 hover:scale-105">
                  <div className="text-3xl font-thin mb-3">
                    {this.getData('has_business_continuity_plan')
                      ? intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.true' }).toUpperCase()
                      : intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.custom' })}
                  </div>
                  <div className="text-sm font-light tracking-wide opacity-80 uppercase">
                    {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.continuity_plan_label' })}
                  </div>
                </div>
              </div>
              <div className="border-t border-white/30 pt-6">
                <p className="text-base font-light opacity-90">
                  {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.confidential_notice' })}
                </p>
              </div>
            </div>
          </Card>

          {/* About This Teaser */}
          <Card className="p-6">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-medium text-[var(--succession-text-primary)] mb-2">
                  {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.about_teaser_title' })}
                </h3>
                <p className="text-sm text-[var(--succession-text-secondary)] leading-relaxed">
                  {intl.formatMessage({ id: 'succession.buyer_room.deal_teaser.about_teaser_description' })}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }
}

DealTeaserSlideshow.propTypes = {
  deal: PropTypes.object.isRequired,
  company: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  dealInterest: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(DealTeaserSlideshow); 